import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from "@common/axios";
import {Alert, Row} from 'antd';
import CircularLoader from "@common/CircularLoader";
import {ContentContext} from "@context/ContentContext";

const DesmosPlayer = props => {
    const {mediaId, conceptId, dialogMedia = true, displayHeader = true} = props;
    const {content, dialogMediaObject, setDialogMediaObject} = useContext(ContentContext);
    const mediaConceptId = conceptId ? conceptId : content.currentConcept.conceptId;
    const [media, setMedia] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isMatrixCalculator, setMatrixCalculator] = useState(false);
    const [error, setError] = useState(null);
    const desmosRef = useRef(null);
    const calculatorRef = useRef(null);
    const debouncedSaveRef = useRef(null);

    useEffect(() => {
        if (!mediaId || !mediaConceptId) {
            setLoading(false);
            setMedia(null);
            return;
        }

        setLoading(true);
        setError(null);

        axios.get('/web/ui/media/media', {params: {mediaId: mediaId, conceptId: mediaConceptId}})
            .then(res => {
                if (dialogMedia && !dialogMediaObject?.mediaName) {
                    setDialogMediaObject({...dialogMediaObject, mediaName: res.data.mediaName});
                }
                setMedia(res.data)
            })
            .catch(err => {
                console.error('Desmos media load failed:', err);
                setError(err);
            }).finally(() => setLoading(false));
    }, [mediaId, mediaConceptId]);

    useEffect(() => {
        if ((!media || !desmosRef.current) && !dialogMediaObject?.isGenericCalculator) return;

        setLoading(true);

        if (!calculatorRef.current) {
            const desmosCalcType = media ? media.mediaSubType : dialogMediaObject?.mediaSubType ? dialogMediaObject?.mediaSubType : true;

            if (desmosCalcType === 'DESMOS_MATRIX') {
                setMatrixCalculator(true);
                setLoading(false);
                return;
            }

            switch (desmosCalcType) {
                case 'DESMOS_GEOMETRY':
                    calculatorRef.current = Desmos.Geometry(desmosRef.current, {actions: true});
                    break;
                case 'DESMOS_SCIENTIFIC':
                    calculatorRef.current = Desmos.ScientificCalculator(desmosRef.current, {actions: true});
                    break;
                default:
                    calculatorRef.current = Desmos.GraphingCalculator(desmosRef.current, {actions: true});
                    break;
            }
        }

        const calculator = calculatorRef.current;
        const calculatorStateAttr = desmosRef.current.getAttribute('data-desmos');

        const applyState = (state) => {
            try {
                calculator.setState(state);
                setLoading(false);
            } catch (e) {
                console.warn('Invalid calculator state:', e);
            }
        };

        const saveState = () => {
            if (desmosRef.current) {
                setLoading(false);
                const state = calculator.getState();
                desmosRef.current.setAttribute('data-desmos', JSON.stringify(state));
            }
        };

        // Custom debounce utility
        const debounce = (func, delay) => {
            let timeout;
            return function (...args) {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), delay);
            };
        };

        // Attach debounced observer and store the reference
        debouncedSaveRef.current = debounce(saveState, 500);
        calculator.observeEvent('change', debouncedSaveRef.current);

        if (mediaId && !calculatorStateAttr) {
            axios.get('/lessonSection/displayGraphingCalculator.action', {params: {mediaId: mediaId}})
                .then(response => applyState(response.data))
                .catch(err => {
                    console.error('Failed to load Desmos graph state:', err);
                    setError(new Error("Unable to load the Desmos activity state."));
                });
        } else if (calculatorStateAttr) {
            try {
                applyState(JSON.parse(calculatorStateAttr));
            } catch (e) {
                console.warn('Failed to parse stored calculator state:', e);
            }
        }

        return () => {
            if (calculatorRef.current && debouncedSaveRef.current) {
                calculatorRef.current.unobserveEvent('change', debouncedSaveRef.current);
            }
        };
    }, [media]);

    if (error) {
        return (
            <Alert
                role="alert"
                type="error"
                showIcon
                message="Failed to load Desmos activity"
                description={error.message || "An unexpected error occurred."}
            />
        );
    }

    return (
        <div className={'ch-desmos-player-wrapper'}>
            {loading && (
                <Row>
                    <CircularLoader displayInElement={true}/>
                </Row>
            )}
            {displayHeader && (
                <>
                    {(!dialogMediaObject && media?.mediaName) && (
                        <h3>{media.mediaName}</h3>
                    )}
                    {media?.mediaDescription && (
                        <div className={'ch-media-description padding-b--m'}>
                            {media.mediaDescription}
                        </div>
                    )}
                </>
            )}
            <div ref={desmosRef} className={'ch-desmos-player'} role="application">
                {isMatrixCalculator &&
                    <iframe src={'/utils/desmos-matrix/matrix.html'} />
                }
            </div>
        </div>
    );
};

export default DesmosPlayer;
